import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Text,
  Flex,
  Button,
  useToast,
  Textarea,
  SimpleGrid,
  IconButton,
  Tooltip,
  useColorModeValue,
  Badge,
  CircularProgress,
  CircularProgressLabel,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Image,
  useDisclosure,
  Heading,
  HStack,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner
} from "@chakra-ui/react";
import Card from "../components/Card/Card";
import CardBody from "../components/Card/CardBody";
import CardHeader from "../components/Card/CardHeader";
import { useAuth } from "../context/AuthContext";
import { DndContext, closestCenter, DragOverlay } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FaMagic, FaPen, FaCopy, FaTrash, FaGripVertical, FaCheck, FaSearch, FaRobot, FaLock, FaGlobe, FaFire } from "react-icons/fa";
import { FiActivity } from "react-icons/fi";
import { BsChatDots } from "react-icons/bs";
import GenerateExcelFile from "./GenerateExcelFile";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import BulletAuditReport, { BulletAuditResult } from "./BulletAuditReport";
import MonkeyRoastReport, { MonkeyRoastResult } from "./MonkeyRoastReport";
import MarketplaceSelector, { marketplaces } from "./MarketplaceSelector";
import { CreditCostBadge } from "./CreditCostBadge";
import { productBriefToPrompt } from "../utils/productBriefToPrompt";

// Import Profile Images
import abuProfile from "../assets/img/abuProfile.jpg";
import mengaProfile from "../assets/img/mengaProfile.jpg";

const MotionBox = motion(Box);


// --- Bullet Card Component ---
interface BulletCardProps {
  id: string;
  index: number;
  text: string;
  isEditing: boolean;
  onEdit: () => void;
  onSave: (newText: string) => void;
  onCancel: () => void;
  onRegenerate: (agent: 'abu' | 'menga') => void;
  onCopy: () => void;
  onDelete: () => void;
  isRegenerating: boolean;
  dragHandleProps?: any;
  // Audit Props
  onSingleAudit: () => void;
  auditScore?: number;
  isAuditing?: boolean;
  // Chat Prop
  onAnalyzeBullet: (text: string, index: number, agent: 'abu'|'menga') => void;
}

const BulletCard: React.FC<BulletCardProps> = ({
  id,
  index,
  text,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onRegenerate,
  onCopy,
  onDelete,
  isRegenerating,
  dragHandleProps,
  onSingleAudit,
  auditScore,
  isAuditing,
  onAnalyzeBullet
}) => {
  const { t } = useTranslation();
  const [localText, setLocalText] = useState(text);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync local text when prop changes (unless editing)
  useEffect(() => {
    if (!isEditing) setLocalText(text);
  }, [text, isEditing]);

  // Glassmorphism Card Style
  const cardBg = useColorModeValue("white", "rgba(26, 32, 44, 0.6)");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const glowColor = "rgba(66, 153, 225, 0.4)";
  
  // Score Color
  let scoreColor = "red.400";
  if (auditScore && auditScore >= 50) scoreColor = "orange.400";
  if (auditScore && auditScore >= 75) scoreColor = "green.400";

  const LOREM_IPSUM_START = "Lorem ipsum dolor sit amet";
  const isLocked = typeof text === 'string' && text.startsWith(LOREM_IPSUM_START);

  return (
    <MotionBox
      layoutId={id}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.5 }}
      transition={{ duration: 0.2 }}
      bg={cardBg}
      border="1px solid"
      borderColor={isEditing ? "blue.400" : borderColor}
      borderRadius="xl"
      p={4}
      position="relative"
      backdropFilter="blur(10px)"
      boxShadow={isEditing ? `0 0 15px ${glowColor}` : "lg"}
      _hover={{
        borderColor: isEditing ? "blue.400" : "blue.300",
        boxShadow: `0 0 20px ${glowColor}`,
        transform: "translateY(-2px)"
      }}
      h="100%"
      display="flex"
      flexDirection="column"
    >
      {/* Header: Grip + Badge */}
      <Flex justify="space-between" align="center" mb={2}>
        <Flex align="center" gap={2}>
            <Box 
                {...dragHandleProps} 
                cursor="grab" 
                p={1} 
                borderRadius="md" 
                _hover={{ bg: "whiteAlpha.200" }}
                color="gray.500"
            >
                <FaGripVertical />
            </Box>
            <Badge colorScheme="teal" variant="solid" borderRadius="full" px={2}>
                #{index + 1}
            </Badge>
            {/* Audit Score Badge/Progress if exists */}
            {auditScore !== undefined && (
                 <Popover trigger="hover" placement="top">
                    <PopoverTrigger>
                        <Box cursor="pointer">
                            <Badge 
                                colorScheme={auditScore >= 75 ? "green" : auditScore >= 50 ? "orange" : "red"} 
                                borderRadius="full" 
                                variant="outline" 
                                display="flex" 
                                alignItems="center" 
                                gap={1}
                            >
                                <Icon as={FiActivity} /> {auditScore}/100
                            </Badge>
                        </Box>
                    </PopoverTrigger>
                    <PopoverContent bg="gray.800" borderColor="gray.600">
                        <PopoverHeader fontWeight="bold" borderBottomWidth="0">{t('qualityScore')}</PopoverHeader>
                        <PopoverBody fontSize="sm">
                            {t('qualityScoreDesc', { score: auditScore })}
                        </PopoverBody>
                    </PopoverContent>
                 </Popover>
            )}
        </Flex>
        
        {/* Actions Toolbar (Visible on hover or edit) */}
        <Flex gap={1} opacity={isEditing ? 1 : 0.7} transition="opacity 0.2s">
            {!isEditing && !isLocked && (
                <>
                    {/* Chat Action */}
                    <Menu>
                        <Tooltip label="Discuter avec l'IA" placement="top">
                            <MenuButton
                                as={IconButton}
                                aria-label="Chat"
                                icon={<BsChatDots />}
                                size="xs"
                                variant="ghost"
                                colorScheme="purple"
                            />
                        </Tooltip>
                        <MenuList bg="gray.800" borderColor="gray.700">
                            <MenuItem 
                                icon={<Image src={abuProfile} boxSize="20px" borderRadius="full" objectFit="cover" />} 
                                onClick={() => onAnalyzeBullet(text, index, 'abu')}
                                bg="gray.800" _hover={{ bg: "gray.700" }}
                            >
                                {t('analyzeWith', { name: 'Abu' })}
                            </MenuItem>
                            <MenuItem 
                                icon={<Image src={mengaProfile} boxSize="20px" borderRadius="full" objectFit="cover" />} 
                                onClick={() => onAnalyzeBullet(text, index, 'menga')}
                                bg="gray.800" _hover={{ bg: "gray.700" }}
                            >
                                {t('analyzeWith', { name: 'Menga' })}
                            </MenuItem>
                        </MenuList>
                    </Menu>

                    <Tooltip label="Auditer ce point" placement="top">
                        <Flex as="span" align="center">
                            <IconButton
                                aria-label="Audit"
                                icon={isAuditing ? <CircularProgress isIndeterminate size="20px" color="teal.400" /> : <FiActivity />}
                                size="xs"
                                variant="ghost"
                                colorScheme="cyan"
                                onClick={onSingleAudit}
                                isDisabled={isAuditing}
                            />
                            <CreditCostBadge actionKey="audit" />
                        </Flex>
                    </Tooltip>

                    {/* Regenerate Action - Now with Menu */}
                    <Menu>
                        <Tooltip label={t("regenerate")} placement="top">
                             <Flex as="span" align="center">
                                 <MenuButton
                                    as={IconButton}
                                    aria-label="Regenerate"
                                    icon={<FaMagic />}
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="purple"
                                    isLoading={isRegenerating}
                                 />
                                 <CreditCostBadge actionKey="regenerate_bullet" />
                             </Flex>
                        </Tooltip>
                        <MenuList bg="gray.800" borderColor="gray.700">
                            <MenuItem 
                                icon={<Image src={abuProfile} boxSize="20px" borderRadius="full" objectFit="cover" />} 
                                onClick={() => onRegenerate('abu')}
                                bg="gray.800" _hover={{ bg: "gray.700" }}
                            >
                                {t('regenerateWith', { name: 'Abu' })}
                            </MenuItem>
                            <MenuItem 
                                icon={<Image src={mengaProfile} boxSize="20px" borderRadius="full" objectFit="cover" />} 
                                onClick={() => onRegenerate('menga')}
                                bg="gray.800" _hover={{ bg: "gray.700" }}
                            >
                                {t('regenerateWith', { name: 'Menga' })}
                            </MenuItem>
                        </MenuList>
                    </Menu>

                    <Tooltip label={t("copy")} placement="top">
                        <IconButton
                            aria-label="Copy"
                            icon={<FaCopy />}
                            size="xs"
                            variant="ghost"
                            onClick={onCopy}
                        />
                    </Tooltip>
                    <Tooltip label={t("edit")} placement="top">
                        <IconButton
                            aria-label="Edit"
                            icon={<FaPen />}
                            size="xs"
                            variant="ghost"
                            onClick={onEdit}
                        />
                    </Tooltip>
                    <Tooltip label={t("delete")} placement="top">
                        <IconButton
                            aria-label="Delete"
                            icon={<FaTrash />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={onDelete}
                        />
                    </Tooltip>
                </>
            )}
            {isEditing && (
                <Flex align="center" gap={2}>
                    <Text 
                        fontSize="xs" 
                        color={localText.length > 500 ? "red.400" : localText.length > 450 ? "orange.400" : "green.400"}
                        fontWeight="bold"
                    >
                        {localText.length} / 500
                    </Text>
                    <Button
                        size="xs"
                        leftIcon={<FaCheck />}
                        colorScheme="green"
                        onClick={() => onSave(localText)}
                    >
                        {t("save")}
                    </Button>
                </Flex>
            )}
        </Flex>
      </Flex>

      {/* Content */}
      <Box flex="1" position="relative">
        {isLocked && (
            <Flex 
                position="absolute" 
                top={0} left={0} right={0} bottom={0} 
                align="center" justify="center" 
                zIndex={2}
                bg="blackAlpha.300"
                borderRadius="md"
            >
                <Icon as={FaLock} w={8} h={8} color="whiteAlpha.900" />
            </Flex>
        )}
        {isEditing ? (
            <Box position="relative">
                <Textarea
                    ref={textareaRef}
                    value={localText}
                    onChange={(e) => setLocalText(e.target.value)}
                    rows={6}
                    bg="blackAlpha.200"
                    border="none"
                    _focus={{ ring: 0 }}
                    fontSize="sm"
                />
            </Box>
        ) : (
            <Text 
                fontSize="sm" 
                color="whiteAlpha.900" 
                whiteSpace="pre-wrap" 
                onClick={!isLocked ? onEdit : undefined} 
                cursor={!isLocked ? "pointer" : "not-allowed"}
                _hover={!isLocked ? { color: "blue.200" } : {}}
                transition="color 0.2s"
                filter={isLocked ? "blur(4px)" : "none"}
                userSelect={isLocked ? "none" : "auto"}
            >
                {text}
            </Text>
        )}
      </Box>
    </MotionBox>
  );
};

// --- Sortable Wrapper ---
const GENERATION_STORAGE_KEY = (projectId: string) => `bullet_generation_${projectId}`;
const GENERATION_MAX_AGE_MS = 5 * 60 * 1000; // 5 min

const SortableBulletItem = (props: any) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.id });
    
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999 : 1,
        opacity: isDragging ? 0.5 : 1
    };

    return (
        <div ref={setNodeRef} style={style}>
            <BulletCard {...props} dragHandleProps={{ ...attributes, ...listeners }} />
        </div>
    );
};

// --- Main Component ---
export interface BulletCreatorProps {
    projectId: string | undefined;
    description: string;
    productBrief?: import("../types/productBrief").ProductBrief | null;
    onAnalyzeBullet: (text: string, index: number, agent: 'abu'|'menga') => void;
    projectData?: any; // New prop to avoid double fetch
    onChatOpen?: () => void;
    onRoastComplete?: () => void;
}

const BulletCreator: React.FC<BulletCreatorProps> = ({
  projectId,
  description,
  productBrief,
  onAnalyzeBullet,
  projectData,
  onChatOpen,
  onRoastComplete
}) => {
  const { t } = useTranslation();
  const { currentUser, getToken, userPlan } = useAuth(); // Use getToken for polling
  const toast = useToast();
  
  const [bullets, setBullets] = useState<string[]>([]);

  // Check if user is on free plan
  const isFreePlan = !userPlan || userPlan === 'free';
  const isEmpirePlan = userPlan?.toLowerCase().includes('empire') ?? false;

  // Mode Analyse : projet avec ASIN et bullets Amazon
  const isAnalyzeMode = projectData?.asin && (projectData?.data?.feature_bullets?.length ?? 0) > 0;
  // Bullets déjà générés par notre IA (vs feature_bullets Amazon)
  const hasIAGeneratedBullets = Array.isArray(projectData?.generatedBullets) && projectData.generatedBullets.length > 0;
  // En mode analyse + free : autoriser la première génération (3 bullets)
  const canGenerateInAnalyzeFree = isAnalyzeMode && isFreePlan && !hasIAGeneratedBullets;
  const canUseAuditInAnalyzeFree = isAnalyzeMode && isFreePlan;

  const [loading, setLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [refreshingIndex, setRefreshingIndex] = useState<number | null>(null);

  // Audit States
  const [globalAudit, setGlobalAudit] = useState<BulletAuditResult | null>(null);
  const [isAuditingGlobal, setIsAuditingGlobal] = useState(false);
  const [singleAudits, setSingleAudits] = useState<Record<number, number>>({}); // Index -> Score
  const [auditingIndex, setAuditingIndex] = useState<number | null>(null);
  const [activeJob, setActiveJob] = useState<{ id: string, type: 'full' | 'single', index?: number } | null>(null);
  
  // Generation Polling State
  const [activeGenerationTaskId, setActiveGenerationTaskId] = useState<string | null>(null);

  // Localization States
  const [viewLanguage, setViewLanguage] = useState<string>('fr');
  const [hasLocalizationEnabled, setHasLocalizationEnabled] = useState(false);
  const [availableTranslations, setAvailableTranslations] = useState<string[]>([]);
  const [isTranslating, setIsTranslating] = useState(false);

  // Audit Modal
  const { isOpen: isAuditModalOpen, onOpen: onAuditModalOpen, onClose: onAuditModalClose } = useDisclosure();

  // Monkey Roast state
  const [monkeyRoast, setMonkeyRoast] = useState<MonkeyRoastResult | null>(null);
  const [isRoasting, setIsRoasting] = useState(false);

  const hasProjectImages = Boolean(
    (projectData?.images && projectData.images.length > 0) ||
    (projectData?.data?.images && projectData.data.images.length > 0)
  );
  const hasBulletsForRoast = bullets.length > 0;
  const canRunMonkeyRoast = isEmpirePlan && hasProjectImages && hasBulletsForRoast;

  const loadProjectData = useCallback((data: any) => {
        if (!data) return;

        // Determine View Language (Default to master or marketplace or 'fr')
        const masterLang = data.masterLanguage || data.marketplace || 'fr';
        
        // Ensure state is only set once or when changing projects, but not resetting on updates if user changed it?
        // Actually, we want to respect current viewLanguage if it's valid for this project, otherwise reset.
        // For simplicity, we initialize logic here but might need effect for changes.
        
        const enabled = data.hasLocalizationEnabled || false;
        setHasLocalizationEnabled(enabled);

        // Calculate available translations
        const translations = data.translations ? Object.keys(data.translations) : [];
        if (!translations.includes(masterLang)) translations.push(masterLang);
        setAvailableTranslations(translations);
        
        // Determine which bullets to show based on viewLanguage
        // We need a ref or state to track if we should update viewLanguage? 
        // For now let's assume viewLanguage is managed by user and we just load data for it.
        // But initially, set it to master.
        
        // This logic is tricky inside a callback. 
        // Let's defer bullet loading to an effect dependent on viewLanguage and projectData.
  }, []);

  // Effect to handle data loading whenever projectData OR viewLanguage changes
  useEffect(() => {
      if (!projectData) return;
      
      const data = projectData;
      const masterLang = data.masterLanguage || data.marketplace || 'fr';
      
      // If localization is NOT enabled, we use legacy fields logic
      // If localization IS enabled, we check translations first
      
      const enabled = data.hasLocalizationEnabled || false;
      
      let bulletsToLoad: string[] = [];

      if (enabled) {
          if (data.translations && data.translations[viewLanguage]) {
              bulletsToLoad = data.translations[viewLanguage].bullets || [];
          } else if (viewLanguage === masterLang) {
              // Fallback for master lang if not explicitly in translations map yet (during migration)
              bulletsToLoad = data.generatedBullets || data.context?.bulletPoints || [];
          } else {
              // Translation needed
              bulletsToLoad = []; 
          }
      } else {
          // Legacy Mode
          const ctxBullets = data.context?.bulletPoints;
          const genBullets = data.generatedBullets;
          const dataBullets = data.data?.feature_bullets;
          
          if (Array.isArray(genBullets) && genBullets.length > 0) bulletsToLoad = genBullets;
          else if (Array.isArray(ctxBullets) && ctxBullets.length > 0) bulletsToLoad = ctxBullets;
          else if (Array.isArray(dataBullets) && dataBullets.length > 0) bulletsToLoad = dataBullets;
          
          // Force viewLanguage to sync with legacy marketplace if not enabled
          if (viewLanguage !== masterLang) {
             // We allow viewLanguage to differ only if enabled? 
             // No, let's keep it simple. If disabled, we just show legacy data regardless of viewLanguage state (which shouldn't be changeable)
          }
      }
      
      const clean = (arr: any[]) => arr.filter(b => typeof b === 'string' && b.trim().length > 0);
      let finalBullets = clean(bulletsToLoad);

      // Limitation Free Plan : 2 bullets en mode création, 3 en mode analyse
      const maxBulletsFree = isAnalyzeMode ? 3 : 2;
      if (isFreePlan && finalBullets.length > maxBulletsFree) {
          finalBullets = finalBullets.slice(0, maxBulletsFree);
      }

      setBullets(finalBullets);
      
      // Update Translation Availability
      const translations = data.translations ? Object.keys(data.translations) : [];
      if (!translations.includes(masterLang)) translations.push(masterLang);
      setAvailableTranslations(translations);
      setHasLocalizationEnabled(enabled);

      // Load Monkey Roast if present
      if (data.monkeyRoast) {
            setMonkeyRoast(data.monkeyRoast);
      } else {
            setMonkeyRoast(null);
      }

      // Load Audits (Legacy/Global logic - might need localization update later)
      if (data.bulletAudits) {
            const audits = data.bulletAudits;
            if (audits.global) {
                setGlobalAudit(audits.global);
            }
            const loadedSingleAudits: Record<number, number> = {};
            Object.keys(audits).forEach(key => {
                if (key !== 'global' && !isNaN(Number(key))) {
                    loadedSingleAudits[Number(key)] = audits[key].overallScore;
                }
            });
            setSingleAudits(loadedSingleAudits);
      }
      
      if (data.auditState?.status === 'processing' && data.auditState.jobId) {
            const state = data.auditState;
            setActiveJob({ id: state.jobId, type: state.target || 'full', index: state.bulletIndex });
            if (state.target === 'single' && typeof state.bulletIndex === 'number') {
                setAuditingIndex(state.bulletIndex);
            } else {
                setIsAuditingGlobal(true);
            }
      }

  }, [projectData, viewLanguage]);

  // Restore generation taskId from localStorage on mount (survives refresh)
  useEffect(() => {
    if (!projectId) return;
    try {
      const stored = localStorage.getItem(GENERATION_STORAGE_KEY(projectId));
      if (!stored) return;
      const { taskId, startedAt } = JSON.parse(stored);
      if (!taskId || typeof startedAt !== "number") return;
      const age = Date.now() - startedAt;
      if (age > GENERATION_MAX_AGE_MS) {
        localStorage.removeItem(GENERATION_STORAGE_KEY(projectId));
        return;
      }
      setActiveGenerationTaskId(taskId);
      setLoading(true);
    } catch {
      localStorage.removeItem(GENERATION_STORAGE_KEY(projectId));
    }
  }, [projectId]);

  // Set initial view language
  useEffect(() => {
      if (projectData && !hasLocalizationEnabled) {
          const master = projectData.masterLanguage || projectData.marketplace || 'fr';
          setViewLanguage(master);
      }
  }, [projectData?.id]); // Only on mount/id change

  const handleActivateLocalization = async () => {
      try {
          const token = await getToken();
          await fetch("/.netlify/functions/activateLocalization", {
              method: "POST",
              headers: { 
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${token}` 
              },
              body: JSON.stringify({ projectId })
          });
          toast({ title: t("translationActivated"), status: "success" });
          // Ideally refresh project data here
          // For now we rely on parent refresh or optimistic update if we had setProject
          // We can force a reload via window or callback if available. 
          // Assuming parent ViewProject polls or re-fetches? No, ViewProject doesn't poll.
          // We should ideally call a refresh callback.
          // But 'setHasLocalizationEnabled(true)' locally helps UI update immediately.
          setHasLocalizationEnabled(true);
      } catch (e) {
          toast({ title: t("activationError"), status: "error" });
      }
  };

  const handleTranslate = async () => {
      setIsTranslating(true);
      try {
          const token = await getToken();
          const response = await fetch("/.netlify/functions/translateProject", {
              method: "POST",
              headers: { 
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${token}` 
              },
              body: JSON.stringify({ 
                  projectId, 
                  targetLanguage: viewLanguage, 
                  sourceLanguage: projectData?.masterLanguage || 'fr',
                  contentTypes: ['bullets']
              })
          });
          
          if (!response.ok) throw new Error("Translation failed");
          
          const data = await response.json();
          // data.translations[targetLang].bullets
          const newBullets = data.translations?.[viewLanguage]?.bullets || [];
          setBullets(newBullets);
          
          toast({ title: t("translationCompleted"), status: "success" });
          setAvailableTranslations(prev => [...prev, viewLanguage]);
          
      } catch (e) {
          toast({ title: t("translationError"), status: "error" });
      } finally {
          setIsTranslating(false);
      }
  };

  // --- Audit Polling Effect ---
  useEffect(() => {
      if (!activeJob) return;

      const poll = async () => {
          try {
              const token = await currentUser?.getIdToken();
              const res = await fetch(`/.netlify/functions/auditProject?projectId=${projectId}&jobId=${activeJob.id}`, {
                  headers: { "Authorization": `Bearer ${token}` }
              });
              const data = await res.json();
              
              if (data.status === 'completed') {
                  if (activeJob.type === 'full') {
                      setGlobalAudit(data.result);
                      setIsAuditingGlobal(false);
                  } else if (activeJob.type === 'single' && activeJob.index !== undefined) {
                      setSingleAudits(prev => ({ ...prev, [activeJob.index!]: data.result.overallScore }));
                      setAuditingIndex(null);
                  }
                  setActiveJob(null);
                  toast({ title: t("auditCompleted"), status: "success" });
              } else if (data.status === 'failed') {
                  throw new Error(data.error || "Unknown error");
              }
              // Else: still processing
          } catch (e) {
              console.error("Polling error", e);
              setActiveJob(null);
              setIsAuditingGlobal(false);
              setAuditingIndex(null);
              toast({ title: t("auditError"), status: "error" });
          }
      };

      const interval = setInterval(poll, 3000);
      return () => clearInterval(interval);
  }, [activeJob, projectId, currentUser, toast]);


  // --- Generation Polling Effect ---
  useEffect(() => {
    if (!activeGenerationTaskId) return;

    const pollGeneration = async () => {
        try {
            const token = await getToken();
            const res = await fetch(`/.netlify/functions/checkTaskStatus?taskId=${activeGenerationTaskId}`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            const data = await res.json();

            if (data.status === 'completed') {
                // Compatible avec le nouveau format result: { bullets: string[], credits_used: number }
                // Et l'ancien format (au cas où)
                const newBullets = data.result.bullets || data.result.generatedBullets || [];
                setBullets(newBullets);
                setLoading(false);
                setActiveGenerationTaskId(null);
                if (projectId) localStorage.removeItem(GENERATION_STORAGE_KEY(projectId));
                
                const creditsUsed = data.result.credits_used;
                const creditMsg = creditsUsed ? ` (${t('xCredits', { count: creditsUsed })})` : "";
                
                toast({ 
                    title: t("generationComplete"), 
                    description: `Vos bullet points sont prêts.${creditMsg}`,
                    status: "success",
                    duration: 5000
                });
                
            } else if (data.status === 'failed') {
                throw new Error(data.error || "Generation failed");
            }
            // Else: still processing
        } catch (e: any) {
            console.error("Generation polling error", e);
            setLoading(false);
            setActiveGenerationTaskId(null);
            if (projectId) localStorage.removeItem(GENERATION_STORAGE_KEY(projectId));
            toast({ title: t("generationError"), description: e.message, status: "error" });
        }
    };

    const interval = setInterval(pollGeneration, 2000); // Poll every 2s
    return () => clearInterval(interval);
  }, [activeGenerationTaskId, projectId, getToken, t, toast]);


  const saveBullets = async (updatedBullets: string[]) => {
      try {
          const token = await getToken();
          await fetch("/.netlify/functions/updateBullets", {
              method: "POST", // Le proxy gère la méthode vers l'API
              headers: { 
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${token}` 
              },
              body: JSON.stringify({ projectId, userId: currentUser?.uid, bullets: updatedBullets })
          });
          // Invalidate single audits when bullets change
          setSingleAudits({});
      } catch (err) { console.error("Save error", err); }
  };

  const handleGenerateBullets = async () => {
      // Limitation Free Plan : Pas de régénération complète si des bullets IA existent déjà
      // Exception : mode Analyse - autoriser la première génération (3 bullets)
      if (isFreePlan && hasIAGeneratedBullets && !canGenerateInAnalyzeFree) {
          toast({
              title: t("limitFreePlan"),
              description: t("limitRegeneration"),
              status: "warning",
              duration: 5000,
              isClosable: true,
              position: "top"
          });
          return;
      }

      setLoading(true);
      try {
          const token = await getToken();
          const options: Record<string, unknown> = {
              description: description || (productBrief ? productBriefToPrompt(productBrief, t) : ""),
              ...(productBrief ? { productBrief } : {}),
          };
          if (canGenerateInAnalyzeFree) {
              options.freePlanAnalyzeMode = true;
              options.maxBullets = 3;
          }
          const response = await fetch("/.netlify/functions/triggerGeneration", {
              method: "POST",
              headers: { 
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${token}`
              },
              body: JSON.stringify({ 
                  projectId, 
                  userId: currentUser?.uid, 
                  options
              })
          });
          
          if (!response.ok) {
              const err = await response.json();
              throw new Error(err.error || "Failed to trigger generation");
          }

          const data = await response.json();
          if (data.taskId) {
              setActiveGenerationTaskId(data.taskId);
              if (projectId) {
                  localStorage.setItem(GENERATION_STORAGE_KEY(projectId), JSON.stringify({
                      taskId: data.taskId,
                      startedAt: Date.now()
                  }));
              }
              toast({ title: t("generationStarted"), status: "info", duration: 2000 });
          } else {
              throw new Error("No Task ID returned");
          }

      } catch (e: any) { 
          toast({ title: t("error"), description: e.message, status: "error" }); 
          setLoading(false);
      }
  };

  const handleDragEnd = (event: any) => {
      const { active, over } = event;
      if (active.id !== over.id) {
          const oldIndex = bullets.indexOf(active.id);
          const newIndex = bullets.indexOf(over.id);
          const newBullets = arrayMove(bullets, oldIndex, newIndex);
          setBullets(newBullets);
          saveBullets(newBullets);
      }
  };

  const handleRegenerateSingle = async (index: number, agent: 'abu'|'menga' = 'abu') => {
      setRefreshingIndex(index);
      try {
          const response = await fetch("/.netlify/functions/generateSingleBulletListing", {
              method: "POST",
              headers: { 
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${await currentUser?.getIdToken()}`
              },
              body: JSON.stringify({ 
                  projectId, 
                  userId: currentUser?.uid, 
                  index, 
                  currentBullet: bullets[index], 
                  allBullets: bullets,
                  agent // Pass agent choice
              })
          });
          
          if (!response.ok) throw new Error("Failed to regenerate");

          const data = await response.json();
          const newText = data.generatedBullets || data.bullet;

          if (newText) {
              const newBullets = [...bullets];
              newBullets[index] = newText;
              setBullets(newBullets);
              saveBullets(newBullets);
              toast({ title: t("bulletRewritten"), status: "success" });
          } else {
              throw new Error("Invalid response format: missing bullet text");
          }
      } catch (e) { toast({ title: t("error"), status: "error" }); }
      finally { setRefreshingIndex(null); }
  };
  
  // --- Audit Handlers ---

  const handleMonkeyRoast = async () => {
    if (!canRunMonkeyRoast || !projectId) return;
    setIsRoasting(true);
    setMonkeyRoast(null);
    try {
      const token = await currentUser?.getIdToken();
      const response = await fetch(`/.netlify/functions/roastProject`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ projectId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const errMsg = data.error || data.message || "Roast failed";
        toast({ title: t("monkeyRoastError"), description: errMsg, status: "error" });
        return;
      }
      setMonkeyRoast(data);
      onRoastComplete?.();
      toast({ title: t("monkeyRoastCompleted"), status: "success" });
    } catch (e) {
      console.error(e);
      toast({ title: t("monkeyRoastError"), status: "error" });
    } finally {
      setIsRoasting(false);
    }
  };

  const handleAuditClick = () => {
      if (isFreePlan && !canUseAuditInAnalyzeFree) {
          toast({
              title: t("freePlanFeature"),
              description: t("advancedAuditPremium"),
              status: "info",
              duration: 5000,
              isClosable: true,
              position: "top"
          });
          return;
      }
      onAuditModalOpen();
  };

  const handleGlobalAudit = async (agent: 'abu' | 'menga' = 'abu') => {
      if (isFreePlan && !canUseAuditInAnalyzeFree) return; 

      onAuditModalClose();
      setIsAuditingGlobal(true);
      setGlobalAudit(null);
      try {
          const response = await fetch(`/.netlify/functions/auditProject`, {
              method: "POST",
              headers: { 
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${await currentUser?.getIdToken()}`
              },
              body: JSON.stringify({ projectId, target: "full", bullets, agent })
          });
          
          if (!response.ok) {
              const errData = await response.json().catch(() => ({}));
              const errMsg = errData.error || "Audit start failed";
              
              if (response.status === 429 || (response.status === 500 && errMsg.toLowerCase().includes("surchargé"))) {
                  toast({
                      title: t("serviceOverloaded"),
                      description: t("waitRetry"),
                      status: "warning",
                      duration: 6000,
                      isClosable: true
                  });
                  setIsAuditingGlobal(false);
                  return;
              }
              throw new Error(errMsg);
          }
          
          const data = await response.json();
          const jobId = data.jobId || data.body?.jobId;
          if (jobId) {
              setActiveJob({ id: jobId, type: 'full' });
              toast({ title: t("auditStarted"), status: "info" });
          } else {
              throw new Error("No Job ID returned");
          }
      } catch (e) {
          console.error(e);
          toast({ title: t("auditError"), status: "error" });
          setIsAuditingGlobal(false);
      }
  };

  const handleSingleAudit = async (index: number) => {
      if (isFreePlan && !canUseAuditInAnalyzeFree) {
          toast({
              title: t("freePlanFeature"),
              description: t("singleAuditPremium"),
              status: "info",
              duration: 3000,
              position: "top"
          });
          return;
      }

      setAuditingIndex(index);
      try {
          const response = await fetch(`/.netlify/functions/auditProject`, {
              method: "POST",
              headers: { 
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${await currentUser?.getIdToken()}`
              },
              body: JSON.stringify({ 
                  projectId, 
                  target: "single", 
                  bulletIndex: index,
                  bulletContent: bullets[index],
                  bullets 
              })
          });

          if (!response.ok) {
              const errData = await response.json().catch(() => ({}));
              const errMsg = errData.error || "Audit start failed";
              
              if (response.status === 429 || (response.status === 500 && errMsg.toLowerCase().includes("surchargé"))) {
                  toast({
                      title: t("serviceOverloaded"),
                      description: t("waitRetry"),
                      status: "warning",
                      duration: 6000,
                      isClosable: true
                  });
                  setAuditingIndex(null);
                  return;
              }
              throw new Error(errMsg);
          }

           const data = await response.json();
           const jobId = data.jobId || data.body?.jobId;
           if (jobId) {
               setActiveJob({ id: jobId, type: 'single', index });
               toast({ title: t("analysisStarted"), status: "info" });
           } else {
               throw new Error("No Job ID returned");
           }

      } catch (e) {
          console.error(e);
          toast({ title: t("auditError"), status: "error" });
          setAuditingIndex(null);
      }
  };

  const handleIgnoreRule = async (issueId: string, action: 'ignore' | 'restore') => {
      try {
          const response = await fetch("/.netlify/functions/ignoreAuditRule", {
              method: "POST",
              headers: { 
                  "Content-Type": "application/json",
                  "Authorization": `Bearer ${await currentUser?.getIdToken()}`
              },
              body: JSON.stringify({ 
                  projectId, 
                  issueId, 
                  auditKey: "global", 
                  action 
              })
          });

          if (!response.ok) throw new Error("Failed to update rule");

          const data = await response.json();
          if (data.updatedAudit) {
              setGlobalAudit(data.updatedAudit);
              toast({ 
                  title: action === 'ignore' ? t("ruleIgnored") : t("ruleRestored"), 
                  status: "success" 
              });
          }
      } catch (e) {
          console.error(e);
          toast({ title: t("error"), description: "Impossible de modifier la règle", status: "error" });
      }
  };

  return (
    <Box mt={10}>
        <Flex justify="space-between" align="center" mb={6}>
            <Box>
                <Flex align="center" gap={4}>
                    <Text fontSize="2xl" fontWeight="bold" bgGradient="linear(to-r, teal.300, blue.400)" bgClip="text">
                        {t("bulletListing")}
                    </Text>
                    
                    {/* Localization Controls */}
                    {hasLocalizationEnabled ? (
                         <MarketplaceSelector 
                            currentLanguage={viewLanguage} 
                            availableLanguages={availableTranslations}
                            onLanguageChange={setViewLanguage}
                         />
                    ) : (
                        bullets.length > 0 && (
                            <Tooltip label="Activer la traduction en plusieurs langues">
                                <Button 
                                    size="sm" 
                                    leftIcon={<FaGlobe />} 
                                    onClick={handleActivateLocalization}
                                    colorScheme="purple"
                                    variant="ghost"
                                >
                                    {t("activateTranslation")}
                                </Button>
                            </Tooltip>
                        )
                    )}
                </Flex>
                <Text fontSize="sm" color="gray.400">{t("optimizingBulletPoints")}</Text>
            </Box>
            
            <HStack spacing={3}>
                <Tooltip 
                    label={isFreePlan && hasIAGeneratedBullets && !canGenerateInAnalyzeFree ? t("limitRegeneration") : canGenerateInAnalyzeFree ? t("generate3BulletsFreeAnalyze") : ""}
                    hasArrow
                    placement="top"
                    shouldWrapChildren
                >
                    <Button
                        size="lg"
                        bgGradient={isFreePlan && hasIAGeneratedBullets && !canGenerateInAnalyzeFree ? "linear(to-r, gray.400, gray.500)" : "linear(to-r, teal.400, blue.500)"}
                        _hover={isFreePlan && hasIAGeneratedBullets && !canGenerateInAnalyzeFree ? {} : { bgGradient: "linear(to-r, teal.500, blue.600)", boxShadow: "0 0 20px rgba(49, 151, 149, 0.4)" }}
                        color="white"
                        leftIcon={isFreePlan && hasIAGeneratedBullets && !canGenerateInAnalyzeFree ? <FaLock /> : <FaMagic />}
                        onClick={handleGenerateBullets}
                        isLoading={loading}
                        isDisabled={isFreePlan && hasIAGeneratedBullets && !canGenerateInAnalyzeFree}
                        _disabled={{
                            opacity: 0.7,
                            cursor: "not-allowed",
                            bgGradient: "linear(to-r, gray.400, gray.500)"
                        }}
                    >
                        {t("generate")}
                        <CreditCostBadge actionKey="generate_bullets" />
                    </Button>
                </Tooltip>
                {onChatOpen && (
                    <Tooltip label="Chat with Abu (Beta)" placement="top">
                        <Button
                            size="lg"
                            colorScheme="purple"
                            variant="outline"
                            leftIcon={<BsChatDots />}
                            onClick={onChatOpen}
                        >
                            Abu
                            <CreditCostBadge actionKey="chat_message" />
                        </Button>
                    </Tooltip>
                )}
            </HStack>
        </Flex>

        {/* Persistent banner when bullets are being generated (survives refresh) */}
        {loading && (
            <Alert
                status="info"
                variant="subtle"
                borderRadius="lg"
                mb={6}
                bg="teal.500"
                color="white"
                alignItems="center"
            >
                <AlertIcon as={Spinner} boxSize="5" color="white" />
                <Box flex="1">
                    <AlertTitle>{t("generationInProgress")}</AlertTitle>
                    <AlertDescription>{t("generationEta")}</AlertDescription>
                </Box>
            </Alert>
        )}

        {hasLocalizationEnabled && !availableTranslations.includes(viewLanguage) && bullets.length === 0 ? (
            <Flex 
                direction="column" 
                align="center" 
                justify="center" 
                p={10} 
                border="2px dashed" 
                borderColor="gray.600" 
                borderRadius="xl" 
                bg="whiteAlpha.50"
            >
                <Icon as={FaGlobe} w={12} h={12} color="gray.400" mb={4} />
                <Heading size="md" mb={2} color="gray.300">
                    {t("missingTranslation")}
                </Heading>
                <Text color="gray.500" mb={6}>
                    {t("generateOptimizedVersion")}
                </Text>
                <Button 
                    colorScheme="purple" 
                    size="lg" 
                    leftIcon={<FaMagic />} 
                    onClick={handleTranslate}
                    isLoading={isTranslating}
                    loadingText="Traduction en cours..."
                >
                    {t("translateTo", { lang: viewLanguage.toUpperCase() })}
                    <CreditCostBadge actionKey="translate" />
                </Button>
            </Flex>
        ) : (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={bullets} strategy={rectSortingStrategy}>
                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                        {bullets.map((bullet, index) => (
                            <SortableBulletItem
                                key={bullet} // Using content as key can be buggy if duplicates exist, ideally use index or unique ID
                                id={bullet}
                                index={index}
                                text={bullet}
                                isEditing={editingIndex === index}
                                onEdit={() => setEditingIndex(index)}
                                onSave={(txt: string) => {
                                    const newBullets = [...bullets];
                                    newBullets[index] = txt;
                                    setBullets(newBullets);
                                    saveBullets(newBullets);
                                    setEditingIndex(null);
                                }}
                                onCancel={() => setEditingIndex(null)}
                                onRegenerate={(agent: 'abu'|'menga') => handleRegenerateSingle(index, agent)}
                                onCopy={() => { navigator.clipboard.writeText(bullet); toast({ title: t("copied"), status: "success" }); }}
                                onDelete={() => {
                                    const newBullets = bullets.filter((_, i) => i !== index);
                                    setBullets(newBullets);
                                    saveBullets(newBullets);
                                }}
                                isRegenerating={refreshingIndex === index}
                                // Audit Props
                                onSingleAudit={() => handleSingleAudit(index)}
                                auditScore={singleAudits[index]}
                                isAuditing={auditingIndex === index}
                                // Chat Prop
                                onAnalyzeBullet={onAnalyzeBullet}
                            />
                        ))}
                        
                        {/* Limitation Free Plan : Carte incitative */}
                        {isFreePlan && bullets.length > 0 && (
                             <Box 
                                border="1px dashed" 
                                borderColor="yellow.500" 
                                borderRadius="xl" 
                                p={6} 
                                bg="whiteAlpha.50"
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                textAlign="center"
                                minH="200px"
                                position="relative"
                                overflow="hidden"
                            >
                                <Box 
                                    position="absolute" 
                                    top={0} left={0} right={0} bottom={0} 
                                    bgGradient="linear(to-br, transparent 0%, yellow.900 100%)" 
                                    opacity={0.2} 
                                />
                                <Icon as={FaLock} w={10} h={10} color="yellow.400" mb={4} zIndex={1} />
                                <Heading size="md" mb={2} color="white" zIndex={1}>
                                    {t("unlockFullListing")}
                                </Heading>
                                <Text fontSize="sm" color="gray.300" mb={5} zIndex={1}>
                                    {t("freePlanLimitBullets")}
                                </Text>
                                <Button 
                                    colorScheme="yellow" 
                                    size="md" 
                                    fontWeight="bold"
                                    onClick={() => window.location.href = '/subscribe'}
                                    zIndex={1}
                                    _hover={{ transform: "scale(1.05)" }}
                                >
                                    {t("unlockNow")}
                                </Button>
                            </Box>
                        )}
                    </SimpleGrid>
                </SortableContext>
            </DndContext>
        )}

        {/* Global Audit Section */}
        <Box mt={8}>
            <Flex justify="center" gap={4}>
                <Tooltip 
                    label={isFreePlan && !canUseAuditInAnalyzeFree ? t("advancedAuditPremium") : ""}
                    hasArrow
                    placement="top"
                    shouldWrapChildren
                >
                    <Button 
                        variant="outline" 
                        colorScheme={isFreePlan && !canUseAuditInAnalyzeFree ? "gray" : "purple"}
                        onClick={handleAuditClick}
                        isLoading={isAuditingGlobal}
                        loadingText={isAuditingGlobal ? "Analyse en cours..." : undefined}
                        leftIcon={isFreePlan && !canUseAuditInAnalyzeFree ? <FaLock /> : <FiActivity />}
                        size="lg"
                        borderRadius="full"
                        px={8}
                        opacity={isFreePlan && !canUseAuditInAnalyzeFree ? 0.7 : 1}
                        cursor={isFreePlan && !canUseAuditInAnalyzeFree ? "not-allowed" : "pointer"}
                        _hover={isFreePlan && !canUseAuditInAnalyzeFree ? {} : undefined}
                    >
                        {t("startAdvancedAudit")}
                        <CreditCostBadge actionKey="audit" />
                    </Button>
                </Tooltip>
                <Button
                    variant="ghost"
                    colorScheme="gray"
                    onClick={() => {
                        const allText = bullets.join("\n\n");
                        navigator.clipboard.writeText(allText);
                        toast({ title: t("allCopied"), status: "success" });
                    }}
                    leftIcon={<FaCopy />}
                    size="lg"
                    borderRadius="full"
                    px={6}
                >
                    {t("copyAll")}
                </Button>
                <Tooltip
                    label={
                      !isEmpirePlan ? t("monkeyRoastEmpireOnly") :
                      !hasProjectImages ? t("monkeyRoastNeedImages") :
                      !hasBulletsForRoast ? t("monkeyRoastNeedBullets") : ""
                    }
                    hasArrow
                    placement="top"
                    shouldWrapChildren
                >
                    <Button
                        variant="outline"
                        colorScheme={canRunMonkeyRoast ? "orange" : "gray"}
                        onClick={handleMonkeyRoast}
                        leftIcon={!canRunMonkeyRoast ? <FaLock /> : <FaFire />}
                        size="lg"
                        borderRadius="full"
                        px={6}
                        isLoading={isRoasting}
                        loadingText={t("monkeyRoastInProgress")}
                        isDisabled={!canRunMonkeyRoast}
                        opacity={!canRunMonkeyRoast ? 0.7 : 1}
                        cursor={!canRunMonkeyRoast ? "not-allowed" : "pointer"}
                        _hover={!canRunMonkeyRoast ? {} : undefined}
                    >
                        {t("monkeyRoast")}
                        <CreditCostBadge actionKey="roast" />
                    </Button>
                </Tooltip>
            </Flex>
            <AnimatePresence>
                {(globalAudit || isAuditingGlobal) && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                         <BulletAuditReport 
                            audit={globalAudit} 
                            isLoading={isAuditingGlobal} 
                            onIgnoreRule={handleIgnoreRule}
                         />
                    </motion.div>
                )}
            </AnimatePresence>
            <AnimatePresence>
                {(monkeyRoast || isRoasting) && (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                        <MonkeyRoastReport roast={monkeyRoast} isLoading={isRoasting} />
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>

        {/* Audit Selection Modal */}
        <Modal isOpen={isAuditModalOpen} onClose={onAuditModalClose} size="3xl" isCentered>
            <ModalOverlay backdropFilter="blur(5px)" />
            <ModalContent bg="gray.900" border="1px solid" borderColor="gray.700">
                <ModalHeader textAlign="center">{t("chooseAuditor")}</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={10}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        {/* Abu Card */}
                        <Box 
                            border="1px solid" 
                            borderColor="purple.500" 
                            borderRadius="xl" 
                            p={6} 
                            cursor="pointer"
                            bg="whiteAlpha.50"
                            _hover={{ bg: "whiteAlpha.100", transform: "scale(1.02)", boxShadow: "0 0 20px rgba(128, 90, 213, 0.3)" }}
                            transition="all 0.2s"
                            onClick={() => handleGlobalAudit('abu')}
                            textAlign="center"
                        >
                            <VStack spacing={4}>
                                <Image src={abuProfile} boxSize="100px" borderRadius="full" border="2px solid" borderColor="purple.400" objectFit="cover" />
                                <Heading size="md" color="purple.300">Abu</Heading>
                                <Text fontSize="sm" color="gray.300">
                                    {t("abuDesc")}
                                </Text>
                                <Badge colorScheme="purple" fontSize="lg" px={3} borderRadius="full">{t("xCredits", { count: 10 })}</Badge>
                            </VStack>
                        </Box>

                        {/* Menga Card */}
                        <Box 
                            border="1px solid" 
                            borderColor="blue.500" 
                            borderRadius="xl" 
                            p={6} 
                            cursor="pointer"
                            bg="whiteAlpha.50"
                            _hover={{ bg: "whiteAlpha.100", transform: "scale(1.02)", boxShadow: "0 0 20px rgba(66, 153, 225, 0.3)" }}
                            transition="all 0.2s"
                            onClick={() => handleGlobalAudit('menga')}
                            textAlign="center"
                        >
                            <VStack spacing={4}>
                                <Image src={mengaProfile} boxSize="100px" borderRadius="full" border="2px solid" borderColor="blue.400" objectFit="cover" />
                                <Heading size="md" color="blue.300">Menga</Heading>
                                <Text fontSize="sm" color="gray.300">
                                    {t("mengaDesc")}
                                </Text>
                                <Badge colorScheme="blue" fontSize="lg" px={3} borderRadius="full">{t("xCredits", { count: 15 })}</Badge>
                            </VStack>
                        </Box>
                    </SimpleGrid>
                </ModalBody>
            </ModalContent>
        </Modal>
    </Box>
  );
};

export default BulletCreator;